import React, { useState } from 'react';
import { Book, Database, Shield, FileJson, TrendingUp, Map, Brain, Upload, AlertTriangle } from 'lucide-react';

export default function BiharDocs() {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: 'Overview', icon: Book },
    { id: 'architecture', title: 'Architecture', icon: Database },
    { id: 'security', title: 'Security', icon: Shield },
    { id: 'connections', title: 'DB Connections', icon: Database },
    { id: 'import', title: 'Data Import', icon: Upload },
    { id: 'schema', title: 'Schema & Mapping', icon: FileJson },
    { id: 'outliers', title: 'Outlier Detection', icon: AlertTriangle },
    { id: 'normalization', title: 'Data Normalization', icon: TrendingUp },
    { id: 'insights', title: 'Insights Dashboard', icon: Brain },
    { id: 'ai-insights', title: 'AI Query Assistant', icon: Brain },
    { id: 'manual-explorer', title: 'Manual Explorer', icon: Map }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Sidebar Navigation */}
      <div className="w-64 bg-slate-800/50 backdrop-blur-sm border-r border-slate-700 fixed h-screen overflow-y-auto">
        <div className="p-6 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white">Bihar Analytics</h2>
          <p className="text-sm text-slate-400 mt-1">Documentation</p>
        </div>
        
        <nav className="p-4">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                  activeSection === section.id
                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                    : 'text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{section.title}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="ml-64 flex-1 p-8">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* Overview Section */}
          <section id="overview" className="scroll-mt-8">
            <h1 className="text-4xl font-bold text-white mb-4">Bihar Sports Analytics Platform</h1>
            <p className="text-lg text-slate-300 mb-6">
              A comprehensive platform for analyzing athlete performance data, detecting outliers, 
              normalizing measurements, and generating AI-powered insights.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <Upload className="w-8 h-8 text-blue-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">Multi-Source Import</h3>
                <p className="text-sm text-slate-400">Import from Excel, CSV, or MongoDB databases</p>
              </div>
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <TrendingUp className="w-8 h-8 text-green-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">Smart Normalization</h3>
                <p className="text-sm text-slate-400">Automatic data cleaning and outlier detection</p>
              </div>
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <Brain className="w-8 h-8 text-purple-400 mb-3" />
                <h3 className="font-semibold text-white mb-2">AI Insights</h3>
                <p className="text-sm text-slate-400">Generate intelligent analysis and recommendations</p>
              </div>
            </div>
          </section>

          {/* Architecture Section */}
          <section id="architecture" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">System Architecture</h2>
            <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
              <h3 className="text-xl font-semibold text-white mb-4">Technology Stack</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-white mb-2">Frontend</h4>
                  <ul className="list-disc list-inside text-slate-300 space-y-1">
                    <li>React 18 with functional components and hooks</li>
                    <li>Tailwind CSS for styling</li>
                    <li>Lucide React for icons</li>
                    <li>Client-side data visualization</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Backend</h4>
                  <ul className="list-disc list-inside text-slate-300 space-y-1">
                    <li>Next.js API routes (serverless)</li>
                    <li>Vercel deployment platform</li>
                    <li>MongoDB proxy server on Hetzner (port 5001)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">Database</h4>
                  <ul className="list-disc list-inside text-slate-300 space-y-1">
                    <li>PostgreSQL (Neon) for primary data storage</li>
                    <li>MongoDB for external data sources</li>
                    <li>JSONB columns for flexible athlete data</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Security Section */}
          <section id="security" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">Security</h2>
            <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-white mb-2">Database Security</h4>
                  <ul className="list-disc list-inside text-slate-300 space-y-1">
                    <li>MongoDB authentication with user credentials (bihar_admin)</li>
                    <li>PostgreSQL connection via secure Neon serverless</li>
                    <li>Environment variables for sensitive credentials</li>
                    <li>Firewall rules on Hetzner server (ports 27017, 5001)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-2">API Security</h4>
                  <ul className="list-disc list-inside text-slate-300 space-y-1">
                    <li>CORS enabled for trusted origins</li>
                    <li>Input validation on all API endpoints</li>
                    <li>SQL injection prevention via parameterized queries</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* DB Connections Section */}
          <section id="connections" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">Database Connections</h2>
            <div className="space-y-6">
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">PostgreSQL (Primary Database)</h3>
                <div className="bg-slate-900/50 p-4 rounded-lg font-mono text-sm">
                  <p className="text-slate-300">Provider: Neon Serverless</p>
                  <p className="text-slate-300">Connection: DATABASE_URL environment variable</p>
                  <p className="text-slate-300">Tables: bihar_datasets</p>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">MongoDB (External Data Source)</h3>
                <div className="bg-slate-900/50 p-4 rounded-lg font-mono text-sm space-y-2">
                  <p className="text-slate-300">Host: 65.109.9.212:27017</p>
                  <p className="text-slate-300">Database: bihar_sports</p>
                  <p className="text-slate-300">Collection: athletes</p>
                  <p className="text-slate-300">Auth: bihar_admin / bihar2024</p>
                </div>
                <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-300">
                    <strong>Note:</strong> MongoDB connections use a proxy server (port 5001) to avoid 
                    Vercel serverless timeout issues with the MongoDB driver.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Data Import Section */}
          <section id="import" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">Data Import</h2>
            <div className="space-y-6">
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Supported Formats</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Excel (.xlsx)</h4>
                    <p className="text-sm text-slate-300">Upload Excel files with athlete data</p>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">CSV (.csv)</h4>
                    <p className="text-sm text-slate-300">Import comma-separated value files</p>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">MongoDB</h4>
                    <p className="text-sm text-slate-300">Connect to MongoDB databases directly</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Import Process</h3>
                <ol className="list-decimal list-inside space-y-3 text-slate-300">
                  <li>Upload file or provide MongoDB connection details</li>
                  <li>System validates data format and structure</li>
                  <li>Automatic data normalization and outlier detection</li>
                  <li>Percentile calculations for all metrics</li>
                  <li>Data saved to PostgreSQL database</li>
                  <li>Dataset card created with statistics</li>
                  <li>Dataset available in Manual Explorer</li>
                </ol>
              </div>
            </div>
          </section>

          {/* Schema & Mapping Section */}
          <section id="schema" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">Schema & Mapping</h2>
            <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
              <h3 className="text-xl font-semibold mb-4">Athlete Data Schema</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-700">
                  <thead className="bg-slate-900/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Field</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Description</th>
                    </tr>
                  </thead>
                  <tbody className="bg-slate-800/50 backdrop-blur-sm divide-y divide-slate-700">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">student_id</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">String</td>
                      <td className="px-6 py-4 text-sm text-slate-300">Unique student identifier</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">full_name</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">String</td>
                      <td className="px-6 py-4 text-sm text-slate-300">Student full name</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">height_cm</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">Number</td>
                      <td className="px-6 py-4 text-sm text-slate-300">Height in centimeters</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">weight_kg</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">Number</td>
                      <td className="px-6 py-4 text-sm text-slate-300">Weight in kilograms</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">vertical_jump_cm</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">Number</td>
                      <td className="px-6 py-4 text-sm text-slate-300">Vertical jump height</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">broad_jump_cm</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">Number</td>
                      <td className="px-6 py-4 text-sm text-slate-300">Broad jump distance</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">sprint_30m_sec</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">Number</td>
                      <td className="px-6 py-4 text-sm text-slate-300">30m sprint time (lower is better)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">shuttle_run_sec</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">Number</td>
                      <td className="px-6 py-4 text-sm text-slate-300">Shuttle run time (lower is better)</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">football_throw_m</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">Number</td>
                      <td className="px-6 py-4 text-sm text-slate-300">Football throw distance</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono">run_800m_sec</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">Number</td>
                      <td className="px-6 py-4 text-sm text-slate-300">800m run time (lower is better)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Outlier Detection Section */}
          <section id="outliers" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">Outlier Detection</h2>
            <div className="space-y-6">
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Detection Algorithm</h3>
                <p className="text-slate-300 mb-4">
                  The system uses the Interquartile Range (IQR) method to detect statistical outliers:
                </p>
                <div className="bg-slate-900/50 p-4 rounded-lg font-mono text-sm space-y-2">
                  <p>IQR = Q3 - Q1</p>
                  <p>Lower Bound = Q1 - (1.5 × IQR)</p>
                  <p>Upper Bound = Q3 + (1.5 × IQR)</p>
                </div>
                <p className="text-slate-300 mt-4">
                  Values outside these bounds are flagged as outliers and may be normalized.
                </p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Outlier Categories</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full mt-1"></div>
                    <div>
                      <h4 className="font-semibold text-white">Extreme Outliers</h4>
                      <p className="text-sm text-slate-300">Values far outside normal range, automatically normalized</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mt-1"></div>
                    <div>
                      <h4 className="font-semibold text-white">Moderate Outliers</h4>
                      <p className="text-sm text-slate-300">Values slightly outside normal range, flagged for review</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-1"></div>
                    <div>
                      <h4 className="font-semibold text-white">Normal Values</h4>
                      <p className="text-sm text-slate-300">Values within expected range</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Normalization Section */}
          <section id="normalization" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">Data Normalization</h2>
            <div className="space-y-6">
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Normalization Rules</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">Height (cm)</h4>
                    <p className="text-sm text-slate-300">Range: 100-220 cm. Values outside are capped to bounds.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Weight (kg)</h4>
                    <p className="text-sm text-slate-300">Range: 20-150 kg. Extreme values normalized to dataset median.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Performance Metrics</h4>
                    <p className="text-sm text-slate-300">
                      Jump heights, sprint times, and distances are normalized using IQR method. 
                      Impossible values (e.g., negative times) are removed.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Percentile Calculation</h3>
                <p className="text-slate-300 mb-4">
                  After normalization, percentiles are calculated for each metric to enable comparative analysis:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Higher percentiles indicate better performance (except for time-based metrics)</li>
                  <li>For sprint/run times, lower values get higher percentiles</li>
                  <li>Percentiles range from 0-100</li>
                  <li>Used for talent identification and performance tracking</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Insights Dashboard Section */}
          <section id="insights" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">Insights Dashboard</h2>
            <p className="text-lg text-slate-300 mb-6">
              The Insights Dashboard provides comprehensive statistical analysis with correlation analysis, 
              performance tier distribution, sport suitability analysis, and interactive visualizations.
            </p>

            <div className="space-y-6">
              {/* Overview */}
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Overview</h3>
                <p className="text-slate-300 mb-4">
                  The Insights Dashboard automatically analyzes your athlete dataset and provides:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Correlation analysis between performance metrics</li>
                  <li>Performance tier categorization (Elite, Excellent, Good, Average, Developing)</li>
                  <li>Sport suitability distribution (Football, Kabaddi, Athletics, Cycling, Volleyball)</li>
                  <li>Statistical outlier identification</li>
                  <li>Top performers tracking for each metric</li>
                  <li>High-potential athlete identification</li>
                  <li>Interactive charts with click-through athlete lists</li>
                </ul>
              </div>

              {/* Correlation Analysis */}
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Correlation Analysis</h3>
                <p className="text-slate-300 mb-4">
                  Uses Pearson correlation coefficient to identify relationships between metrics:
                </p>
                <div className="bg-slate-900/50 p-4 rounded-lg font-mono text-sm space-y-2 mb-4">
                  <p className="text-slate-300">r = Σ[(xi - x̄)(yi - ȳ)] / √[Σ(xi - x̄)² × Σ(yi - ȳ)²]</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-1"></div>
                    <div>
                      <h4 className="font-semibold text-white">Strong Correlation (|r| ≥ 0.7)</h4>
                      <p className="text-sm text-slate-300">Strong relationship between metrics (e.g., Height &harr; Weight)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mt-1"></div>
                    <div>
                      <h4 className="font-semibold text-white">Moderate Correlation (0.4 ≤ |r| &lt; 0.7)</h4>
                      <p className="text-sm text-slate-300">Moderate relationship (e.g., Vertical Jump &harr; Broad Jump)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 bg-slate-500 rounded-full mt-1"></div>
                    <div>
                      <h4 className="font-semibold text-white">Weak Correlation (|r| &lt; 0.4)</h4>
                      <p className="text-sm text-slate-300">Little to no relationship between metrics</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Tiers */}
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Performance Tier Distribution</h3>
                <p className="text-slate-300 mb-4">
                  Athletes are categorized based on their average percentile across all metrics:
                </p>
                <div className="space-y-3">
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <h4 className="font-semibold text-green-400">Elite (90-100th percentile)</h4>
                    <p className="text-sm text-slate-300">Top 10% performers - exceptional athletes across all metrics</p>
                  </div>
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <h4 className="font-semibold text-blue-400">Excellent (75-90th percentile)</h4>
                    <p className="text-sm text-slate-300">Strong performers with consistent high performance</p>
                  </div>
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <h4 className="font-semibold text-yellow-400">Good (50-75th percentile)</h4>
                    <p className="text-sm text-slate-300">Above-average performers with development potential</p>
                  </div>
                  <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <h4 className="font-semibold text-orange-400">Average (25-50th percentile)</h4>
                    <p className="text-sm text-slate-300">Mid-range performers requiring targeted training</p>
                  </div>
                  <div className="p-3 bg-slate-500/10 border border-slate-500/30 rounded-lg">
                    <h4 className="font-semibold text-slate-400">Developing (0-25th percentile)</h4>
                    <p className="text-sm text-slate-300">Early-stage athletes with significant growth opportunity</p>
                  </div>
                </div>
              </div>

              {/* Sport Suitability */}
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Sport Suitability Analysis</h3>
                <p className="text-slate-300 mb-4">
                  Athletes are scored for each sport based on relevant performance metrics. 
                  A score of 50+ indicates strong suitability:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-blue-400 mb-2">Football</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Sprint speed (30m &lt; 6s): 30 points</li>
                      <li>• Broad jump (&gt; 180cm): 25 points</li>
                      <li>• Shuttle run (&lt; 16s): 25 points</li>
                      <li>• Vertical jump (&gt; 25cm): 20 points</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-green-400 mb-2">Kabaddi</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Size (weight &gt; 50kg, height &gt; 160cm): 25 points</li>
                      <li>• Shuttle run (&lt; 17s): 30 points</li>
                      <li>• Endurance (800m &lt; 300s): 25 points</li>
                      <li>• Broad jump (&gt; 160cm): 20 points</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-orange-400 mb-2">Athletics</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Sprint speed (30m &lt; 6s): 35 points</li>
                      <li>• Broad jump (&gt; 180cm): 30 points</li>
                      <li>• Vertical jump (&gt; 30cm): 25 points</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-red-400 mb-2">Cycling</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Endurance (800m &lt; 240s): 35 points</li>
                      <li>• Broad jump (&gt; 150cm): 20 points</li>
                      <li>• Optimal weight (45-70kg): 20 points</li>
                      <li>• Height (&gt; 160cm): 15 points</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-purple-400 mb-2">Volleyball</h4>
                    <ul className="text-sm text-slate-300 space-y-1">
                      <li>• Height (&gt; 170cm): 35 points</li>
                      <li>• Vertical jump (&gt; 35cm): 30 points</li>
                      <li>• Shuttle run (&lt; 16s): 20 points</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Interactive Features */}
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Interactive Charts & Athlete Lists</h3>
                <p className="text-slate-300 mb-4">
                  All charts are interactive - click any visualization to see detailed athlete lists:
                </p>
                <div className="space-y-3">
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Performance Tier Bars</h4>
                    <p className="text-sm text-slate-300">Click to see all athletes in that tier with their average percentiles</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Sport Distribution Bars</h4>
                    <p className="text-sm text-slate-300">Click to see athletes suitable for that sport with their suitability scores</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Top Performer Cards</h4>
                    <p className="text-sm text-slate-300">Click to see top 10 athletes for that metric with values and percentiles</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Elite/Potential Cards</h4>
                    <p className="text-sm text-slate-300">Click to see detailed lists with athlete IDs, names, and performance data</p>
                  </div>
                </div>
              </div>

              {/* Data Storage */}
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Percentile Storage & Performance</h3>
                <p className="text-slate-300 mb-4">
                  All percentiles are calculated during data import and stored in the database JSONB column:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Percentiles calculated once during import, not on every query</li>
                  <li>Stored in <code className="bg-slate-900 px-2 py-1 rounded">athlete_data</code> JSONB column</li>
                  <li>Enables fast queries and instant insights generation</li>
                  <li>In-memory caching (5 minutes) for sub-100ms load times</li>
                  <li>First load: ~2-3 seconds, subsequent loads: &lt;100ms</li>
                </ul>
              </div>

              {/* API Endpoint */}
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">API Endpoint</h3>
                <div className="bg-slate-900/50 p-4 rounded-lg font-mono text-sm">
                  <p className="text-green-400">GET /api/bihar-insights?dataset_id=sample</p>
                </div>
                <p className="text-slate-300 mt-4">Returns comprehensive insights including:</p>
                <ul className="list-disc list-inside text-slate-300 space-y-1 mt-2">
                  <li>correlations (array of metric pairs with correlation coefficients)</li>
                  <li>performanceTiers (athlete counts and lists per tier)</li>
                  <li>sportDistribution (athlete counts and lists per sport)</li>
                  <li>outlierAnalysis (statistical outliers per metric)</li>
                  <li>topPerformers (top 10 athletes per metric)</li>
                  <li>improvementOpportunities (high-potential athletes)</li>
                  <li>percentileDistribution (histogram data)</li>
                  <li>metricRanges (min, max, avg, median per metric)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* AI Query Assistant Section */}
          <section id="ai-insights" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">AI Query Assistant</h2>
            <div className="space-y-6">
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Natural Language Queries</h3>
                <p className="text-slate-300 mb-4">
                  The AI Query Assistant allows you to ask questions in natural language:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>"Show top 5 athletes by height"</li>
                  <li>"Athletes with vertical jump over 50cm"</li>
                  <li>"Find athletes with 800m run time under 4 minutes"</li>
                  <li>"Show athletes with weight between 40-60kg"</li>
                  <li>"Top 3 performers in broad jump"</li>
                </ul>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Using AI Query Assistant</h3>
                <ol className="list-decimal list-inside space-y-3 text-slate-300">
                  <li>Select a dataset from the dropdown</li>
                  <li>Type your question in natural language</li>
                  <li>System parses query and applies filters</li>
                  <li>Results displayed in interactive table</li>
                  <li>Click athlete rows for detailed profiles</li>
                </ol>
              </div>
            </div>
          </section>

          {/* Manual Explorer Section */}
          <section id="manual-explorer" className="scroll-mt-8">
            <h2 className="text-3xl font-bold text-white mb-4">Manual Explorer</h2>
            <div className="space-y-6">
              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Features</h3>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Browse all imported datasets</li>
                  <li>View detailed athlete profiles</li>
                  <li>Compare athletes across metrics</li>
                  <li>Filter and search functionality</li>
                  <li>Export data for external analysis</li>
                  <li>Visualize performance distributions</li>
                </ul>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-lg border border-slate-700">
                <h3 className="text-xl font-semibold mb-4">Data Visualization</h3>
                <p className="text-slate-300 mb-4">
                  The Manual Explorer provides interactive visualizations including:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Performance Charts</h4>
                    <p className="text-sm text-slate-300">Visual representation of athlete metrics</p>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Percentile Rankings</h4>
                    <p className="text-sm text-slate-300">Comparative performance indicators</p>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Outlier Highlights</h4>
                    <p className="text-sm text-slate-300">Visual flags for exceptional values</p>
                  </div>
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">Dataset Statistics</h4>
                    <p className="text-sm text-slate-300">Summary metrics and distributions</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
